import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import { supabase } from '../lib/db';
import { format } from 'date-fns';
import { Calendar, Clock, AlertCircle, CheckCircle, XCircle, QrCode, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';

interface FormattedAppointment {
  id: string;
  service_id: string;
  service_name: string;
  provider_name: string;
  counter_name: string;
  queue_number: string;
  status: string;
  scheduled_start_time: string;
  customer_name: string;
  customer_phone: string | null;
}

const AppointmentsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [appointments, setAppointments] = useState<FormattedAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'completed' | 'cancelled'>('upcoming');
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchAppointments();
    }
  }, [user]);

  const fetchAppointments = async () => {
    try {
      const { data, error } = await supabase
        .from('queue_entries')
        .select(`
          id,
          queue_number,
          status,
          scheduled_start_time,
          customer_name,
          customer_phone,
          services!inner (
            id,
            name
          ),
          service_counters!inner (
            name
          ),
          provider_profiles!left (
            business_name
          )
        `)
        .eq('user_id', user?.id)
        .eq('entry_source', 'online')
        .order('scheduled_start_time', { ascending: true });

      if (error) throw error;

      const formattedAppointments = (data as any[]).map(entry => ({
        id: entry.id,
        service_id: entry.services.id,
        service_name: entry.services.name,
        provider_name: entry.provider_profiles.business_name,
        counter_name: entry.service_counters.name,
        queue_number: entry.queue_number,
        status: entry.status,
        scheduled_start_time: entry.scheduled_start_time,
        customer_name: entry.customer_name,
        customer_phone: entry.customer_phone
      }));

      setAppointments(formattedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    try {
      if (!user) {
        toast.error('You must be logged in to cancel appointments');
        return;
      }

      // Update queue entry status
      const { error: queueError } = await supabase
        .from('queue_entries')
        .update({ status: 'cancelled' })
        .eq('id', appointmentId)
        .eq('user_id', user.id);

      if (queueError) throw queueError;

      // Cancel pending notifications
      const { error: notificationError } = await supabase
        .from('notifications')
        .update({ 
          status: 'cancelled',
          metadata: {
            cancelled_at: new Date().toISOString()
          },
          updated_at: new Date().toISOString()
        })
        .eq('entity_type', 'booking')
        .eq('entity_id', appointmentId)
        .eq('user_id', user.id)
        .in('status', ['pending']);

      if (notificationError) {
        console.error('Error updating notifications:', notificationError);
        // Don't throw the error as the appointment was already cancelled
      }

      toast.success('Appointment cancelled successfully');
      fetchAppointments(); // Refresh the list
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'text-blue-200 bg-blue-900/40 border border-blue-700/60';
      case 'in_progress':
        return 'text-amber-200 bg-amber-900/40 border border-amber-700/60';
      case 'completed':
        return 'text-green-200 bg-green-900/40 border border-green-700/60';
      case 'cancelled':
        return 'text-red-200 bg-red-900/40 border border-red-700/60';
      default:
        return 'text-gray-200 bg-gray-700/60 border border-gray-600/70';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="w-5 h-5" />;
      case 'in_progress':
        return <Calendar className="w-5 h-5" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    switch (activeTab) {
      case 'upcoming':
        return ['scheduled', 'in_progress'].includes(appointment.status);
      case 'completed':
        return appointment.status === 'completed';
      case 'cancelled':
        return appointment.status === 'cancelled';
      default:
        return true;
    }
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="theme-page container mx-auto px-4 py-8 max-w-4xl">
      <div className="theme-card overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">My Appointments</h1>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {['upcoming', 'completed', 'cancelled'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === tab
                    ? 'border-b-2 border-primary-500 text-primary-600'
                    : 'text-gray-400 hover:text-gray-200 hover:border-gray-500'
                }`}
              >
                {t(tab)}
              </button>
            ))}
          </nav>
        </div>

        {filteredAppointments.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-300 text-lg">No {activeTab} appointments</p>
          </div>
        ) : (
          filteredAppointments.map((appointment) => (
            <div key={appointment.id} className="p-6 hover:bg-gray-100/70 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                      {getStatusIcon(appointment.status)}
                      <span className="ml-1">{t(appointment.status)}</span>
                    </span>
                    <span className="ml-2 text-sm text-gray-400">#{appointment.queue_number}</span>
                  </div>
                  
                  <h3 className="text-lg font-medium text-gray-900">{appointment.service_name}</h3>
                  
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-gray-400">
                      <span className="font-medium">Provider:</span> {appointment.provider_name}
                    </p>
                    <p className="text-sm text-gray-400">
                      <span className="font-medium">Counter:</span> {appointment.counter_name}
                    </p>
                    <p className="text-sm text-gray-400">
                      <span className="font-medium">Date & Time:</span>{' '}
                      {format(new Date(appointment.scheduled_start_time), 'PPpp')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {appointment.status === 'scheduled' && (
                    <button
                      onClick={() => handleCancelAppointment(appointment.id)}
                      className="px-3 py-1 text-sm text-red-300 hover:text-red-200 hover:bg-red-900/40 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  {['scheduled', 'in_progress'].includes(appointment.status) && (
                    <button
                      onClick={() => setSelectedAppointmentId(appointment.id)}
                      className="inline-flex items-center justify-center text-primary-400 hover:text-primary-300"
                    >
                      <QrCode className="w-5 h-5 mr-1" />
                      <span className="text-sm">QR code</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* QR Code Modal */}
      {selectedAppointmentId && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="theme-card p-6 relative max-w-sm w-full">
            <button
              className="absolute top-4 right-4 text-gray-300 hover:text-white"
              onClick={() => setSelectedAppointmentId(null)}
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold mb-4">{t('scanToCheckIn')}</h3>
            <div className="flex items-center justify-center">
              {(() => {
                const appointment = appointments.find((a) => a.id === selectedAppointmentId);
                if (!appointment) return null;
                return (
                  <div className="space-y-6">
                    <QRCode
                      value={appointment.id}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      style={{ height: "auto", maxWidth: "100%", width: "200px" }}
                      viewBox={`0 0 200 200`}
                    />
                    <div className="text-center">
                      <p className="text-sm text-gray-400 mt-4">Queue Number</p>
                      <p className="text-xl font-bold text-gray-900">{appointment.queue_number}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage; 